import React from 'react';
import { StyleSheet, Text, TouchableOpacity, ViewStyle, Platform } from 'react-native';
import PlatformIcon from './PlatformIcon';
import { MODULE_COLORS } from '@cubemeet/core';

/**
 * PillButton 组件属性
 */
export interface PillButtonProps {
  /**
   * 图标名称（Material Icons）
   */
  iconName: string;
  /**
   * 按钮文本
   */
  text: string;
  /**
   * 点击事件
   */
  onPress: () => void;
  /**
   * 按钮变体
   * - primary: 主要按钮（填充色）
   * - secondary: 次要按钮（边框）
   */
  variant?: 'primary' | 'secondary';
  /**
   * 是否禁用
   */
  disabled?: boolean;
  /**
   * 自定义样式
   */
  style?: ViewStyle;
  /**
   * Optional React node to render on the left side (overrides iconName if provided).
   * Example: <Image source={{uri: '/assets/icons/web-chat.png'}} />
   */
  leftElement?: React.ReactNode;
}

/**
 * PillButton 组件
 * 
 * 药丸形状的按钮组件，带图标和文本
 */
const PillButton: React.FC<PillButtonProps> = ({
  iconName,
  text,
  onPress,
  variant = 'primary',
  disabled = false,
  style,
  leftElement,
}) => {
  return (
    <TouchableOpacity
      style={[
        styles.button,
        variant === 'secondary' && styles.secondaryButton,
        disabled && styles.disabledButton,
        style,
      ]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
    >
      {leftElement ? (
        // render provided left element (e.g. an Image) when present
        <>{leftElement}</>
      ) : (
        <PlatformIcon
          name={iconName}
          size={16}
          color={variant === 'secondary' ? MODULE_COLORS.travel : '#fff'}
          family="MaterialIcons"
        />
      )}
      <Text
        style={[
          styles.text,
          variant === 'secondary' && styles.secondaryText,
          disabled && styles.disabledText,
        ]}
      >
        {text}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: MODULE_COLORS.travel,
    borderRadius: 20,
    // Use CSS boxShadow on web to avoid react-native-web deprecation warnings for shadow* props
    ...(Platform.select ? Platform.select({
      web: { boxShadow: '0 2px 6px rgba(74, 134, 247, 0.12)' },
      default: { shadowColor: MODULE_COLORS.travel, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 4, elevation: 3 },
    }) : { shadowColor: MODULE_COLORS.travel, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 4, elevation: 3 }),
  },
  secondaryButton: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
  },
  disabledButton: {
    backgroundColor: '#E5E7EB',
    ...(Platform.select ? Platform.select({ web: { boxShadow: 'none' }, default: { shadowOpacity: 0, elevation: 0 } }) : { shadowOpacity: 0, elevation: 0 }),
  },
  text: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  secondaryText: {
    color: MODULE_COLORS.travel,
  },
  disabledText: {
    color: '#9CA3AF',
  },
});

export default PillButton;
